/**
 * chat-director/director — the orchestrator that makes a big chatty world
 * affordable.
 *
 * Given current Beat[] + a roster of visible/regular speakers + the player
 * context, it decides WHO speaks about WHICH beats, assembles BATCHED
 * VoiceRequests (one call → a short multi-character exchange), fills a look-ahead
 * BUFFER during idle that streams out at a readable pace, tracks a BudgetState so
 * breadth degrades gracefully (fresh LLM lines → template floor) as tokens run
 * low, caches identical requests briefly, and biases toward freshness so the town
 * never reads canned.
 *
 * Depends ONLY on @/shared + an injected Voice pair (DI). Never imports a concrete
 * LLMVoice; never mutates canon. All randomness flows through an injected core Rng,
 * so a given (beats, roster, seed) plays out deterministically.
 */
import type {
  Beat,
  BudgetState,
  BufferedExchange,
  ChatChannel,
  DirectorPlanInput,
  PlayerContext,
  SpeakerContext,
  Voice,
  VoiceLine,
  VoiceRequest,
  VoiceResponse,
} from '@/shared';
import type { Rng } from '@/core/rng.js';
import { makeRng } from '@/core/rng.js';
import { planSpeakers, type SpeakerPlan } from './select.js';
import {
  chargeTokens,
  estimateBatchTokens,
  isOverBudget,
  loudestPriority,
  shouldUseLlmForBatch,
} from './budget.js';
import { ExchangeCache } from './cache.js';

/** A speaker roster + player slice the director needs (composed @/shared types). */
export interface DirectorContext {
  /** The speakers eligible to talk on each channel (visible + regulars). */
  readonly rosterByChannel: Readonly<Record<ChatChannel, readonly SpeakerContext[]>>;
  readonly player: PlayerContext;
}

/** Wiring for a ChatDirector: the two Voice backends + a clock + a seed. */
export interface DirectorDeps {
  /** Primary voice (LLM in prod; a stub in tests). Injected — never constructed here. */
  readonly llm: Voice;
  /** The fallback floor; covers degraded breadth + offline/over-budget. */
  readonly template: Voice;
  /** Injected clock (ms). Defaults to Date.now; pass a fake for determinism. */
  readonly now?: () => number;
  /** Seed for the director's deterministic speaker-shuffle RNG. */
  readonly seed?: number;
  /** Brief request-cache TTL in ms. */
  readonly cacheTtlMs?: number;
  /** Window in which a beat won't be re-voiced (anti duplicate-spam). */
  readonly recentlyVoicedWindowMs?: number;
  /**
   * Window in which an identical (normalized) line won't be spoken twice — the
   * fix for the bug where two NPCs emitted the SAME canned line. Defaults to the
   * recently-voiced window.
   */
  readonly recentlySpokenWindowMs?: number;
}

let idCounter = 0;
function nextId(channel: ChatChannel, n: number): string {
  return `ex-${channel}-${n}-${idCounter++}`;
}

export class ChatDirector {
  private readonly llm: Voice;
  private readonly template: Voice;
  private readonly now: () => number;
  private readonly cache: ExchangeCache;
  private readonly recentlyVoicedWindowMs: number;
  private readonly recentlySpokenWindowMs: number;
  private readonly rng: Rng;

  /** Per-channel look-ahead buffers (FIFO; streamed out one at a time). */
  private readonly buffers: Map<ChatChannel, BufferedExchange[]> = new Map();
  /**
   * Per-channel queue of PLAYER-originated beats (a chat message, a show-off) that
   * jump to the front of the next fill so NPCs respond to the player promptly. The
   * two-way heart of D14.
   */
  private readonly playerBeats: Map<ChatChannel, Beat[]> = new Map();

  constructor(deps: DirectorDeps) {
    this.llm = deps.llm;
    this.template = deps.template;
    this.now = deps.now ?? Date.now;
    this.cache = new ExchangeCache(deps.cacheTtlMs ?? 30_000);
    this.recentlyVoicedWindowMs = deps.recentlyVoicedWindowMs ?? 60_000;
    this.recentlySpokenWindowMs = deps.recentlySpokenWindowMs ?? this.recentlyVoicedWindowMs;
    this.rng = makeRng(deps.seed ?? 0x5eed);
  }

  /**
   * Queue PLAYER-originated beats (a typed line / quick reply / "show off my mon")
   * so the very next {@link fill} threads them in front of ambient banter and the
   * town reacts in-character. Two-way by construction (D14): the player speaks, and
   * the world answers. The beats are consumed on the next fill of their channel.
   */
  pushPlayerBeats(channel: ChatChannel, beats: readonly Beat[]): void {
    if (beats.length === 0) return;
    const q = this.playerBeats.get(channel) ?? [];
    q.push(...beats);
    this.playerBeats.set(channel, q);
  }

  /** Drain (and clear) the queued player beats for a channel. */
  private takePlayerBeats(channel: ChatChannel): Beat[] {
    const q = this.playerBeats.get(channel);
    if (!q || q.length === 0) return [];
    this.playerBeats.delete(channel);
    return q;
  }

  /** The current buffer for a channel (read-only snapshot). */
  buffer(channel: ChatChannel): readonly BufferedExchange[] {
    return this.buffers.get(channel) ?? [];
  }

  /**
   * Plan a single batched VoiceRequest from a SpeakerPlan: the chosen speakers
   * phrase the chosen beats. The token budget passed to the Voice scales with the
   * batch size (the budget dial). Pure.
   */
  planRequest(plan: SpeakerPlan, ctx: DirectorContext): VoiceRequest {
    return {
      speakers: plan.speakers,
      beats: plan.beats,
      player: ctx.player,
      tokenBudget: estimateBatchTokens(plan.desiredLineCount),
      desiredLineCount: plan.desiredLineCount,
    };
  }

  /**
   * Fill the look-ahead buffer for one channel up to `targetBufferSize`, drawing
   * fresh beats from `input`. Returns the exchanges newly added (also appended to
   * the channel buffer). Called during idle; the result streams out via
   * `nextExchange`.
   *
   * Budget-aware: each batch is voiced by the LLM only if its priority + cost fit
   * the remaining headroom; otherwise the template floor covers it. The supplied
   * BudgetState is treated as immutable; the *updated* budget is returned so the
   * caller can persist spend.
   */
  async fill(
    input: DirectorPlanInput,
    ctx: DirectorContext,
  ): Promise<{ added: BufferedExchange[]; budget: BudgetState }> {
    const channel = input.channel;
    const existing = this.buffers.get(channel) ?? [];
    const playerBeats = this.takePlayerBeats(channel);
    const need = Math.max(0, input.targetBufferSize - existing.length);
    // Player beats always earn a turn — even a full buffer must let the town answer
    // the player. Otherwise honor the buffer cap (no busy over-fill).
    if (need === 0 && playerBeats.length === 0) return { added: [], budget: input.budget };

    const now = this.now();
    const roster = ctx.rosterByChannel[channel] ?? [];

    // Freshness: skip ambient beats just voiced; bias the buffer toward new canon.
    const freshBeats = input.beats.filter(
      (b) => !this.cache.wasRecentlyVoiced(b, now, this.recentlyVoicedWindowMs),
    );

    // Player beats are the player's LIVE input — never suppressed by freshness, and
    // their response thread LEADS the next exchanges (the two-way heart of D14). Plan
    // them separately so even a full buffer still lets the town answer the player.
    const playerPlans =
      playerBeats.length > 0 ? planSpeakers(channel, playerBeats, roster, this.rng) : [];
    const ambientPlans = planSpeakers(channel, freshBeats, roster, this.rng);
    const ambientCap = Math.max(0, need - playerPlans.length);
    const plans = playerPlans.concat(ambientPlans.slice(0, ambientCap));

    let budget = input.budget;
    const added: BufferedExchange[] = [];
    let n = existing.length;

    for (const plan of plans) {
      const request = this.planRequest(plan, ctx);
      const { response, budget: nextBudget } = await this.voiceBatch(request, plan, budget, now);
      budget = nextBudget;

      // De-duplicate output lines: never schedule a line identical (normalized) to
      // one just spoken — the bug where two NPCs emitted the SAME canned line. An
      // exchange that fully collapses to duplicates is dropped (nothing fresh to say).
      const lines = this.dedupeLines(response.lines, now);
      if (lines.length === 0) continue;

      const exchange: BufferedExchange = {
        id: nextId(channel, n++),
        channel,
        lines,
        groundedBeatIds: plan.beats.map((b) => b.id),
        source: response.source,
      };
      added.push(exchange);
    }

    const next = existing.concat(added);
    this.buffers.set(channel, next);
    this.cache.prune(now, this.recentlyVoicedWindowMs);
    return { added, budget };
  }

  /**
   * Drop lines whose text is identical (normalized) to one already in this batch or
   * spoken within the recent window, and record the survivors as spoken. This is the
   * anti-duplication guarantee: the town never repeats the exact same canned line.
   */
  private dedupeLines(lines: readonly VoiceLine[], now: number): VoiceLine[] {
    const kept: VoiceLine[] = [];
    for (const line of lines) {
      const text = line.text.trim();
      if (text.length === 0) continue;
      if (this.cache.wasRecentlySpoken(text, now, this.recentlySpokenWindowMs)) continue;
      kept.push(line);
      this.cache.markSpoken(text, now);
    }
    return kept;
  }

  /**
   * Voice one batch: serve from cache if identical + recent; else pick LLM vs
   * template by budget, call the Voice (non-throwing — falls back to template on
   * any LLM error), charge spent tokens, and record freshness. Returns the chosen
   * response + the updated budget.
   */
  private async voiceBatch(
    request: VoiceRequest,
    plan: SpeakerPlan,
    budget: BudgetState,
    now: number,
  ): Promise<{ response: VoiceResponse; budget: BudgetState }> {
    const cached = this.cache.get(request, now);
    if (cached) {
      this.cache.markVoiced(plan.beats, now);
      return { response: cached, budget };
    }

    const useLlm =
      !isOverBudget(budget) &&
      shouldUseLlmForBatch(loudestPriority(plan.beats), plan.desiredLineCount, budget);

    let response: VoiceResponse;
    if (useLlm) {
      response = await this.tryLlm(request);
    } else {
      response = await this.template.generate(request);
    }

    const nextBudget =
      response.source === 'llm' ? chargeTokens(budget, response.tokensUsed) : budget;
    this.cache.put(request, response, now);
    return { response, budget: nextBudget };
  }

  /** Call the LLM voice; on any failure, degrade to the template floor (never throw). */
  private async tryLlm(request: VoiceRequest): Promise<VoiceResponse> {
    try {
      const res = await this.llm.generate(request);
      // A non-throwing LLM backend may itself report a template fallback.
      return res;
    } catch {
      return this.template.generate(request);
    }
  }

  /**
   * Pull the next ready exchange for a channel to stream out at readable pace, or
   * null if the buffer is empty. FIFO — oldest buffered exchange first.
   */
  next(channel: ChatChannel): BufferedExchange | null {
    const buf = this.buffers.get(channel);
    if (!buf || buf.length === 0) return null;
    const ex = buf.shift()!;
    return ex;
  }

  /** Drop a channel's buffer (e.g. on leaving a zone). */
  clear(channel: ChatChannel): void {
    this.buffers.delete(channel);
  }
}

/** Reset the module-level exchange id counter (deterministic ids in tests). */
export function __resetExchangeIds(): void {
  idCounter = 0;
}
